/**
 * <ELVoiceInterview> — candidate-side v2 interview engine (#2810).
 *
 * The v2 engine runs the interview on an ElevenLabs ConvAI agent (via
 * `@elevenlabs/react`) and records the candidate's camera + mic ourselves
 * (multipart upload to S3) — there is NO Daily/pipecat here. It is rendered in
 * place of the v1 <CustomVoiceInterviewV2> when the company's `interview_engine`
 * is `"elevenlabs"`; the v1 flow is left byte-for-byte unchanged.
 *
 * Flow (all kicked off on the Join click — nothing hits the BE in the waiting
 * room, which just plays a static local clip):
 *   create DB record  → POST /interviews/create
 *   start EL session  → POST /interviews/el/session  → { el_signed_url, s3_upload_config }
 *   connect EL SDK    → startSession({ signedUrl, dynamicVariables: { candidate_name } })
 *     on connect      → startRecording() + startSectionTimers() + POST /interviews/el/el-conversation
 *   record cam+mic    → MediaRecorder(30s chunks) → presigned PUT per ≥5MB part
 *   section timing    → setTimeout → conversation.sendContextualUpdate(...)
 *   on end            → s3-complete → recording-complete → v2/update-status → FeedbackPopup
 *                       (the completion email is sent only when that form is submitted)
 *
 * The agent already carries the system prompt, first message (customGreeting),
 * language and voice — the FE never sends the prompt.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ConversationProvider, useConversation } from "@elevenlabs/react";
import {
    Clock as ClockIcon,
    Phone as PhoneIcon,
    Loader2 as Loader2Icon,
    Settings as SettingsIcon,
    Star as StarIcon,
    TrendingUp as TrendingUpIcon,
    Wifi as WifiIcon,
    CheckCircle2 as CheckCircle2Icon,
    Mic as MicrophoneIcon,
    MicOff as MicOffIcon,
    VideoOff as VideoOffIcon,
    VideoIcon,
    Check as CheckIcon,
    Info as InfoIcon,
    AlertTriangle as AlertTriangleIcon,
} from "lucide-react";

import { Button } from "@component/button";
import AIPersonaOverlay from "@component/ai-persona-overlay";
import EnhancedAudioVisualizer from "@component/enhanced-audio-visualizer";
import LiveStatusBar, { type LiveStatusState } from "@component/live-status-bar";
import TranscriptionPanel from "@component/transcription-panel";
import TranscriptionToggleButton from "@component/transcription-toggle-button";
import SettingsModal from "@component/interview-settings-modal";
import IssuesDialog from "@component/issues-dialog";
import AudioLevelIndicator from "@component/audio-level-indicator";
import SetupChecksPanel from "@component/setup-checks-panel";
import { InterviewConfirmationModal } from "@component/interview-confirmation-modal";
import { InterviewEndConfirmDialogV2 } from "@component/confirm-end-interview-v2";
import FeedbackPopup from "@component/feedback-popup";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@component/dropdown-menu";
import {
    MicrophoneSelector,
    SpeakerSelector,
    CameraSelector,
} from "@component/device-selectors";
import { getBrowserName, stripSpeechTags } from "@util/string";
import { getCookie, setCookie } from "@util/cookie";
import { cn } from "@util/tailwind";
import { catchErrorLog } from "@util/sentry";
import { recordAIInterviewStarted } from "@service/assessment";
import {
    createInterview,
    isClientError,
    resolveConversationId,
    updateInterviewStatusV2,
} from "@service/interview";
import {
    startElSession,
    storeElConversation,
    getElS3UploadPart,
    putElUploadPart,
    completeElS3Upload,
    markElRecordingComplete,
    ElUploadPart,
    ElS3UploadConfig,
} from "@service/interview/el";

/** Every non-final multipart part must be ≥ 5 MB (S3 hard rule). */
const MIN_PART_SIZE = 5 * 1024 * 1024;
/** MediaRecorder chunk cadence — ~30s chunks per the contract. */
const CHUNK_MS = 30_000;

type Phase = "waiting" | "starting" | "live" | "finishing" | "completed" | "error";
type TranscriptMessage = { source: "user" | "ai"; message: string };

export interface ELVoiceInterviewProps {
    /** Assessment id (a.k.a. interviewId). */
    assessmentId: string;
    jobId?: string | null;
    /** Full job-detail object for this application. Serialized (JSON) and sent to
     *  ElevenLabs as the `job_application_detail` dynamic variable so the agent
     *  can reference the role/company context. */
    jobApplicationDetail?: unknown;
    /** User id used for status + feedback-email correlation. */
    userId: string;
    candidateName?: string;
    interviewName?: string;
    /** Short description / scenario shown in the waiting-room header. */
    description?: string;
    aiName?: string;
    /** Assessment `content.interviewLanguage` (e.g. "en-US", "id-ID"). */
    language?: string;
    /** Assessment `content.voice` ("male" | "female") — picks the waiting-room clip. */
    voice?: string;
    /** Sections with durations in SECONDS (`content.sections[].duration`). */
    sections: { title: string; duration: number }[];
    /** Total interview budget in SECONDS (`content.maxDuration`). */
    maxDuration: number;
    /** Called once the v2 completion sequence has finished. */
    onCompletedInterview: () => void;
    onCloseModal?: () => void;
    /** Fired with the final interview status ("Aborted" | "Partial" | "Complete")
     *  so the parent can update the applicant's assessment-completion status
     *  (mirrors custom-voice-interview-v2's onInterviewStatusChange). */
    onInterviewStatusChange?: (status: string) => void;
    /** There's a form assessment step after the interview — feeds the transcript
     *  panel's "step X of Y" NOTE chip (mirrors custom-voice-interview-v2). */
    hasFormAssessment?: boolean;
    /** There's an eligibility / work-authorization step — also counts toward the
     *  transcript panel's "step X of Y" NOTE chip. */
    hasEligibilityCheck?: boolean;
    /** HR test/preview mode — run the UI + agent session but skip ALL candidate
     *  persistence (no recording upload, el-conversation mapping, status write,
     *  scoring trigger, or completion email), since `userId` is a synthetic
     *  non-candidate id. Mirrors custom-voice-interview-v2's `isTestInterview`. */
    isTestMode?: boolean;
}

export default function ELVoiceInterview(props: ELVoiceInterviewProps) {
    return (
        <ConversationProvider>
            <ELVoiceInterviewInner {...props} />
        </ConversationProvider>
    );
}

function ELVoiceInterviewInner({
    assessmentId,
    jobId,
    jobApplicationDetail,
    userId,
    candidateName,
    interviewName,
    description,
    aiName,
    language,
    voice,
    sections,
    maxDuration,
    onCompletedInterview,
    onCloseModal,
    onInterviewStatusChange,
    hasFormAssessment,
    hasEligibilityCheck,
    isTestMode = false,
}: ELVoiceInterviewProps) {
    // i18n — same lightweight 3-arg helper as custom-voice-interview-v2: pick the
    // string for the interview's language (Malay / Indonesian), else fall back to
    // English. `language` carries content.interviewLanguage (e.g. "en-US", "id-ID").
    const isMalay = language === "Malay";
    const isIndonesian = language === "id-ID";
    const t = (en: ReactNode, id: ReactNode, ms: ReactNode): ReactNode => {
        if (isMalay) return ms;
        if (isIndonesian) return id;
        return en;
    };

    const [phase, setPhase] = useState<Phase>("waiting");
    const [errorMessage, setErrorMessage] = useState<string>("");
    // #3460: the interview ran but the BE never recorded it. Distinct from a
    // start-up error because reloading cannot help — it would only risk a second
    // attempt while support recovers the first one.
    const [saveFailed, setSaveFailed] = useState<boolean>(false);
    const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
    const [isEndingCall, setIsEndingCall] = useState<boolean>(false);
    // Per-device permission so the setup checks / badges can show camera and mic
    // independently (one combined getUserMedia rejects entirely if either is
    // denied, which wrongly flagged BOTH as missing). `cameraReady` = both granted,
    // and gates the live preview-recording stream + the Start button.
    const [camGranted, setCamGranted] = useState<boolean>(false);
    const [micGranted, setMicGranted] = useState<boolean>(false);
    const [cameraReady, setCameraReady] = useState<boolean>(false);
    const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
    // UI-only state for the live layout (transcript panel + device settings).
    const [showTranscript, setShowTranscript] = useState<boolean>(true);
    const [showSettings, setShowSettings] = useState<boolean>(false);
    const [selectedMicId, setSelectedMicId] = useState<string>("");
    const [selectedSpeakerId, setSelectedSpeakerId] = useState<string>("");
    const [selectedCameraId, setSelectedCameraId] = useState<string>("");
    // True while a camera switch is in flight — disables the camera selector so a
    // muted-camera user can't spam-switch and race getUserMedia on Android.
    const [cameraSwitching, setCameraSwitching] = useState<boolean>(false);
    const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
    const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);
    const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
    const [isIssuesDialogOpen, setIsIssuesDialogOpen] = useState<boolean>(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    // Confirmation dialog shown when the candidate taps End before the interview is
    // complete (mirrors custom-voice-interview-v2's handleDialogConfirmEndInterview).
    const [isDialogConfirmEndInterview, setIsDialogConfirmEndInterview] = useState<boolean>(false);
    // Show the post-interview feedback form (star rating + issue) for a real candidate
    // once the session completes — mirrors custom-voice-interview-v2, which sets
    // status 'completed' for ANY ended run (Aborted/Partial/Complete) so VideoContainer
    // always shows <FeedbackPopup>. Its onComplete advances the parent (onCloseModal).
    const [showFeedbackPopup, setShowFeedbackPopup] = useState<boolean>(false);
    // Display-only "time to join" countdown shown in the waiting-room setup checks.
    const [joinCountdown, setJoinCountdown] = useState<number>(180);
    // "Concluding" grace window after the AGENT ends the call (mirrors v1's
    // conversation.end → countdown banner). Counts down, then finalizes.
    const [isConcluding, setIsConcluding] = useState<boolean>(false);
    const [concludingCountdown, setConcludingCountdown] = useState<number>(30);
    // Browser-support gate — the EL SDK + MediaRecorder need a modern browser.
    const [isBrowserSupported, setIsBrowserSupported] = useState<boolean>(true);
    // Network quality for the setup-checks "Network" row (Network Information API).
    const [connectionQuality, setConnectionQuality] = useState<"good" | "poor">("good");
    const [downlink, setDownlink] = useState<number | null>(null);
    const [rtt, setRtt] = useState<number | null>(null);
    const [isOnline, setIsOnline] = useState<boolean>(
        typeof navigator !== "undefined" ? navigator.onLine : true,
    );

    // Refs that must survive re-renders / outlive the live phase.
    const cameraStreamRef = useRef<MediaStream | null>(null);
    // Synchronous guard against re-entrant camera switches (a second tap before the
    // disabled state re-renders).
    const cameraSwitchingRef = useRef<boolean>(false);
    const videoElRef = useRef<HTMLVideoElement | null>(null);
    const conversationIdRef = useRef<string>("");
    const startTimeRef = useRef<number>(0);
    const startedRef = useRef<boolean>(false);
    const completedRef = useRef<boolean>(false);
    // Set when session setup fails after connect (e.g. the el_conversation_id
    // mapping can't be persisted) so the disconnect handler doesn't then run the
    // completion sequence and mark an uncorrelatable interview "complete".
    const erroredRef = useRef<boolean>(false);
    // True when the candidate ends via the End button — finalize immediately and
    // skip the "concluding" countdown that an agent-initiated end shows.
    const manualEndRef = useRef<boolean>(false);

    // ── Recording / multipart-upload state ───────────────────────────────────
    const recorderRef = useRef<MediaRecorder | null>(null);
    // Canvas recording pipeline so a mid-interview camera switch reaches the
    // recording: we paint the live preview into a canvas and record the canvas's
    // captureStream (a MediaRecorder can't swap its source track once started).
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawRafRef = useRef<number>(0);
    const recordStreamRef = useRef<MediaStream | null>(null);
    // Watermark image painted top-right into the recording canvas (the local-record
    // equivalent of custom-voice-interview-v2's Daily composition watermark).
    const watermarkImgRef = useRef<HTMLImageElement | null>(null);
    const uploadConfigRef = useRef<ElS3UploadConfig | null>(null);
    const partsRef = useRef<ElUploadPart[]>([]);
    const partNumberRef = useRef<number>(1);
    const bufferRef = useRef<BlobPart[]>([]);
    const bufferSizeRef = useRef<number>(0);
    // Serializes part uploads so PartNumbers stay strictly ordered.
    const uploadChainRef = useRef<Promise<void>>(Promise.resolve());

    // ── Section / end timers ──────────────────────────────────────────────────
    const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    const appendTranscript = (m: TranscriptMessage) =>
        setTranscript((prev) => [...prev, m]);

    // ── ElevenLabs conversation ───────────────────────────────────────────────
    const conversation = useConversation({
        // ── Diagnostics: console.log every agent/SDK action with an [el-agent] prefix
        //    (console only — NOT Sentry). Added to chase a "session ends suddenly with
        //    no agent trigger" report — the onDisconnect reason + the last mode/status
        //    before it are the smoking gun.
        onModeChange: ({ mode }: { mode: string }) => {
            console.log(`[el-agent] modeChange → ${mode}`);
        },
        onStatusChange: ({ status }: { status: string }) => {
            console.log(`[el-agent] statusChange → ${status}`);
        },
        onGuardrailTriggered: () => {
            console.log(`[el-agent] GUARDRAIL triggered (this can end the session)`);
        },
        onInterruption: (p: unknown) => {
            console.log(`[el-agent] interruption ${JSON.stringify(p ?? null)}`);
        },
        onDebug: (p: unknown) => {
            // Verbose SDK internals (WS close codes, etc.) — console only to avoid
            // flooding Sentry; readable via remote debugging.
            console.log("[el-agent][debug]", p);
        },
        onConnect: async ({ conversationId }: { conversationId: string }) => {
            console.log(`[el-agent] connected conversationId=${conversationId} isTestMode=${isTestMode}`);
            // The el_conversation_id mapping is REQUIRED for scoring + AIQM, so the
            // interview must not go live (record / run timers / be completable)
            // until the BE has it. Retry a few times on transient failures, then
            // fail fast — a candidate must never finish an uncorrelatable interview.
            if (!conversationIdRef.current || !conversationId) {
                failSetup("We couldn't start the interview session. Please refresh and try again.");
                return;
            }
            // HR test/preview: skip the el-conversation mapping (scoring-only) and
            // the recording upload — go live straight away. A real candidate must
            // persist the mapping before going live, retrying then failing fast so
            // they never finish an uncorrelatable interview.
            if (!isTestMode) {
                const stored = await storeElConversationWithRetry(
                    conversationIdRef.current,
                    conversationId,
                    3,
                );
                if (!stored) {
                    failSetup("We couldn't initialize the interview session. Please refresh and try again.");
                    return;
                }
            }
            startTimeRef.current = Date.now();
            if (!isTestMode) startRecording();
            startSectionTimers();
            setPhase("live");
        },
        onDisconnect: (details?: { reason?: string; message?: string; context?: unknown }) => {
            const elapsedAtDisconnect = startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 1000) : 0;
            console.log(`[el-agent] DISCONNECT reason=${details?.reason ?? "?"} message=${details?.message ?? "-"} manualEnd=${manualEndRef.current} completed=${completedRef.current} errored=${erroredRef.current} elapsed=${elapsedAtDisconnect}s context=${JSON.stringify(details?.context ?? null)}`);
            if (completedRef.current || erroredRef.current) {
                console.log(`[el-agent] disconnect ignored (already completed/errored)`);
                return;
            }
            // Candidate pressed End → finalize immediately.
            if (manualEndRef.current) {
                console.log(`[el-agent] disconnect → manual End → handleCallEnd()`);
                void handleCallEnd();
                return;
            }
            // Only a CLEAN agent-initiated end (reason "agent" — the agent called
            // end_call) shows the "Interview concluded" countdown banner. Any other
            // disconnect (reason "error", a transient WebSocket drop, etc.) is NOT a
            // real conclusion — previously every non-manual disconnect flashed the
            // banner mid-session. For those, finalize quietly so the recording +
            // transcript are still saved, without the misleading banner.
            if (details?.reason === "agent") {
                // The agent is done — stop the section/wrap timers and run the
                // concluding countdown instead of ending instantly.
                // NOTE: the recording is intentionally NOT stopped here — it keeps
                // running through the ~30s concluding countdown and is stopped by
                // handleCallEnd() only when the countdown elapses. So an
                // agent-initiated end captures ~30s of extra footage (the
                // candidate's reaction after the agent finishes). This is by design;
                // a candidate-pressed End stops immediately via handleCallEnd above.
                console.log(`[el-agent] disconnect reason=agent → concluding countdown (clean agent end)`);
                timersRef.current.forEach(clearTimeout);
                timersRef.current = [];
                setIsConcluding(true);
            } else {
                // UNEXPECTED end (reason "error" / transient WS drop / network blip) —
                // NOT triggered by the agent. This is the "session ends suddenly" path.
                console.log(`[el-agent] disconnect reason=${details?.reason ?? "?"} (NOT agent) → handleCallEnd() — session ended WITHOUT an agent end_call`);
                void handleCallEnd();
            }
        },
        onMessage: ({ message, source }: { message: string; source: "user" | "ai" }) => {
            console.log(`[el-agent] message source=${source}: ${message.slice(0, 200)}`);
            const cleaned = stripSpeechTags(message);
            if (cleaned) appendTranscript({ source, message: cleaned });
        },
        onError: (message?: string, context?: unknown) => {
            console.log(`[el-agent] onError: ${message ?? "(no message)"} context=${JSON.stringify(context ?? null)}`);
            setErrorMessage(t(
                "The interview session hit an error. Please try again.",
                "Sesi wawancara mengalami kesalahan. Silakan coba lagi.",
                "Sesi temu duga mengalami ralat. Sila cuba lagi.",
            ) as string);
            setPhase("error");
        },
    });

    // Keep a stable ref to the live conversation so teardown can always end it.
    const conversationRef = useRef(conversation);
    useEffect(() => {
        if (phase === "live" || phase === "starting") conversationRef.current = conversation;
    }, [phase, conversation]);

    // Stable getter for the candidate's live mic input level (0–1), feeding the
    // "You" audio visualizer. conversation.getInputVolume is a NEW function every
    // render, so passing it straight to the visualizer restarts its rAF loop each
    // render (resetting the meter to ~0 — why the bars looked frozen). A ref holds
    // the latest getter while this callback keeps a stable identity, so the loop
    // runs once and reads real volume each frame.
    const inputVolumeRef = useRef<() => number>(() => 0);
    useEffect(() => {
        inputVolumeRef.current = () => {
            try {
                return conversation.getInputVolume?.() ?? 0;
            } catch {
                return 0;
            }
        };
    }, [conversation]);
    const getInputVolume = useCallback(() => inputVolumeRef.current(), []);

    // Browser-support check on mount — the EL SDK + MediaRecorder need a modern
    // browser; gate the Start button when unsupported (mirrors v1).
    useEffect(() => {
        const { name } = getBrowserName();
        setIsBrowserSupported(
            name === "Chrome" ||
                name === "Firefox" ||
                name === "Safari" ||
                name === "Edge" ||
                name === "Mozilla",
        );
    }, []);

    // Network quality for the setup-checks "Network" row — Network Information API
    // (mirrors custom-voice-interview-v2). effectiveType is the primary signal;
    // downlink is only a last-resort low floor (it's a quantized, often-misleading
    // estimate). Re-reads on connection change / online / offline.
    useEffect(() => {
        const readNetwork = () => {
            const online = typeof navigator !== "undefined" ? navigator.onLine : true;
            setIsOnline(online);
            // @ts-expect-error Navigator connection API is not in lib.dom yet
            const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            if (!c) {
                setDownlink(null);
                setRtt(null);
                setConnectionQuality(online ? "good" : "poor");
                return;
            }
            const effectiveType: string | undefined = c.effectiveType;
            const dl: number | undefined = typeof c.downlink === "number" ? c.downlink : undefined;
            setDownlink(typeof c.downlink === "number" ? c.downlink : null);
            setRtt(typeof c.rtt === "number" ? c.rtt : null);
            if (effectiveType === "slow-2g" || effectiveType === "2g") {
                setConnectionQuality("poor");
            } else if (!effectiveType && dl !== undefined && dl < 0.5) {
                setConnectionQuality("poor");
            } else {
                setConnectionQuality("good");
            }
        };
        readNetwork();
        // @ts-expect-error Navigator connection API is not in lib.dom yet
        const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        c?.addEventListener?.("change", readNetwork);
        window.addEventListener("online", readNetwork);
        window.addEventListener("offline", readNetwork);
        return () => {
            c?.removeEventListener?.("change", readNetwork);
            window.removeEventListener("online", readNetwork);
            window.removeEventListener("offline", readNetwork);
        };
    }, []);

    // Preload the watermark image so it's ready to paint into the recording canvas
    // (see buildCanvasRecordStream). `crossOrigin` is REQUIRED: drawing a non-CORS
    // image would taint the canvas and break captureStream. If the asset lacks CORS
    // headers it simply fails to load and the watermark is skipped (recording still
    // works). URL comes from the same env var custom-voice-interview-v2 uses.
    useEffect(() => {
        const url = import.meta.env.VITE_WATERMARK_IMG_URL;
        if (!url) return;
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = url;
        watermarkImgRef.current = img;
    }, []);

    // ── Camera/mic acquisition for the waiting-room preview + recording ───────
    // Acquire camera and mic SEPARATELY so a partial grant (e.g. camera allowed,
    // mic denied) is reflected per-device — a single combined getUserMedia rejects
    // entirely if either is denied, which wrongly marked both as missing. We then
    // combine whatever tracks we got into the preview/record stream. No full-screen
    // error on denial: the waiting room stays and the setup checks show the
    // per-device "access needed" state + an Enable button (mirrors v1).
    const acquireMedia = useCallback(async () => {
        const tracks: MediaStreamTrack[] = [];
        let camOk = false;
        let micOk = false;
        try {
            const v = await navigator.mediaDevices.getUserMedia({ video: true });
            tracks.push(...v.getVideoTracks());
            camOk = true;
        } catch {
            camOk = false;
        }
        try {
            const a = await navigator.mediaDevices.getUserMedia({ audio: true });
            tracks.push(...a.getAudioTracks());
            micOk = true;
        } catch {
            micOk = false;
        }
        setCamGranted(camOk);
        setMicGranted(micOk);
        // Replace the old stream (retry) and attach whatever we got to the preview.
        cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
        const combined = new globalThis.MediaStream(tracks);
        cameraStreamRef.current = combined;
        if (videoElRef.current) {
            videoElRef.current.srcObject = combined;
        }
        // Both are required to actually run the interview (record + agent).
        setCameraReady(camOk && micOk);
    }, []);

    useEffect(() => {
        void acquireMedia();
    }, [acquireMedia]);

    // Re-attach the stream to the <video> whenever it (re)mounts across phases.
    useEffect(() => {
        if (videoElRef.current && cameraStreamRef.current) {
            videoElRef.current.srcObject = cameraStreamRef.current;
        }
    }, [phase]);

    // Enumerate audio + video devices once camera/mic permission is granted
    // (labels are only populated after getUserMedia succeeds) — feeds the
    // waiting-room camera/mic/speaker dropdowns. Defaults the selection to the
    // live capture device.
    useEffect(() => {
        if (!camGranted && !micGranted) return;
        navigator.mediaDevices
            .enumerateDevices()
            .then((devices) => {
                const ins = devices.filter((d) => d.kind === "audioinput");
                const outs = devices.filter((d) => d.kind === "audiooutput");
                const cams = devices.filter((d) => d.kind === "videoinput");
                setAudioInputs(ins);
                setAudioOutputs(outs);
                setVideoInputs(cams);
                const currentMic = cameraStreamRef.current?.getAudioTracks()[0]?.getSettings().deviceId;
                const currentCam = cameraStreamRef.current?.getVideoTracks()[0]?.getSettings().deviceId;
                setSelectedMicId((prev) => prev || currentMic || ins[0]?.deviceId || "");
                setSelectedSpeakerId((prev) => prev || outs[0]?.deviceId || "");
                setSelectedCameraId((prev) => prev || currentCam || cams[0]?.deviceId || "");
            })
            .catch(() => {});
    }, [camGranted, micGranted]);

    // Switch the capture mic in the waiting room: re-acquire the camera+mic stream
    // with the chosen device so BOTH the preview and the (later) recording use it.
    async function selectMic(deviceId: string) {
        setSelectedMicId(deviceId);
        try {
            const next = await navigator.mediaDevices.getUserMedia({
                video: selectedCameraId ? { deviceId: { exact: selectedCameraId } } : true,
                audio: { deviceId: { exact: deviceId } },
            });
            cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
            cameraStreamRef.current = next;
            if (videoElRef.current) videoElRef.current.srcObject = next;
            // Resume playback — changing srcObject on a mounted <video> doesn't
            // auto-play (muted, so no user gesture needed).
            videoElRef.current?.play().catch(() => {});
        } catch {
            /* keep the current stream if the device can't be opened */
        }
    }

    // Switch the camera (waiting-room dropdown / live Device Settings). Re-acquires
    // the video and repoints the preview; the canvas recorder paints the preview, so
    // the switch is reflected in the recording too. While live we keep the existing
    // audio track (it's wired into the record stream + the EL session) and swap only
    // the video — no second mic, and the recording's audio stays intact.
    async function selectCamera(deviceId: string) {
        // Ignore re-entrant taps while a switch is in flight (the selector is also
        // disabled via `cameraSwitching`; this covers the gap before re-render).
        if (cameraSwitchingRef.current) return;
        const prev = cameraStreamRef.current;
        const prevCameraId =
            prev?.getVideoTracks()[0]?.getSettings().deviceId || selectedCameraId;
        if (deviceId === prevCameraId) return; // no change
        cameraSwitchingRef.current = true;
        setCameraSwitching(true);
        setSelectedCameraId(deviceId);

        // Keep the live audio track alive across the switch — we only swap VIDEO, so
        // the mic (and the recording's audio) is never re-acquired or interrupted.
        const keptAudio = prev?.getAudioTracks() ?? [];

        // Acquire a VIDEO-ONLY stream for the given camera, retrying on
        // NotReadableError (Android frees the camera asynchronously). Audio stays the
        // kept track, so we never ask getUserMedia for two devices at once.
        const acquireVideo = async (camId: string): Promise<MediaStream> => {
            for (let attempt = 0; ; attempt++) {
                try {
                    return await navigator.mediaDevices.getUserMedia({
                        video: { deviceId: { exact: camId } },
                        audio: false,
                    });
                } catch (e) {
                    if (e instanceof Error && e.name === "NotReadableError" && attempt < 2) {
                        await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
                        continue;
                    }
                    throw e;
                }
            }
        };

        const applyVideo = (videoStream: MediaStream) => {
            const composed = new globalThis.MediaStream([
                ...videoStream.getVideoTracks(),
                ...keptAudio,
            ]);
            cameraStreamRef.current = composed;
            if (videoElRef.current) videoElRef.current.srcObject = composed;
            // Changing srcObject on a mounted <video> does NOT auto-resume playback
            // (autoPlay only fires on initial load); without play() the canvas recorder
            // keeps painting the last frozen frame. The element is muted, so no gesture.
            videoElRef.current?.play().catch(() => {});
        };

        try {
            // RELEASE the old camera FIRST. Android can't open a second camera while
            // the first is still held (→ NotReadableError "Could not start video
            // source"), so acquiring the new one before stopping the old silently
            // failed and the camera never switched. Stop only the VIDEO track (keep
            // audio), give the OS a beat to free the sensor, then acquire the new one.
            prev?.getVideoTracks().forEach((t) => t.stop());
            await new Promise((r) => setTimeout(r, 300));
            applyVideo(await acquireVideo(deviceId));
        } catch {
            // New camera couldn't start — restore the PREVIOUS camera so we don't strand
            // a black/frozen preview (we already stopped the old video track).
            try {
                applyVideo(await acquireVideo(prevCameraId));
                setSelectedCameraId(prevCameraId);
            } catch {
                /* nothing more we can safely do */
            }
        } finally {
            cameraSwitchingRef.current = false;
            setCameraSwitching(false);
        }
    }

    // Static waiting-room clip — plays once, no backend (mirrors the training
    // waiting room). Best-effort: autoplay may be blocked until interaction.
    useEffect(() => {
        if (phase !== "waiting") return;
        const audio = new Audio(getWaitingRoomAudioUrl(language, voice));
        audio.play().catch(() => {});
        return () => {
            audio.pause();
        };
    }, [phase, language, voice]);

    // Tick the "time to join" countdown while in the waiting room (display only).
    useEffect(() => {
        if (phase !== "waiting") return;
        const id = window.setInterval(() => {
            setJoinCountdown((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
        return () => window.clearInterval(id);
    }, [phase]);

    // Live timer.
    useEffect(() => {
        if (phase !== "live") return;
        const id = window.setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
        return () => window.clearInterval(id);
    }, [phase]);

    // Warn before a reload/close DURING an active session, so the candidate doesn't
    // lose the interview by accident (native "Reload site? Changes may not be saved"
    // prompt).
    //
    // `finishing` is armed too, and that is the whole point of #3726: it is the
    // phase where leaving used to be MOST expensive and was the only one with no
    // warning at all. The critical calls now go out first, so leaving during the
    // spinner costs the video rather than the interview — still worth a prompt,
    // and no longer a silent loss.
    useEffect(() => {
        if (phase !== "live" && phase !== "starting" && phase !== "finishing") return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [phase]);

    // Concluding countdown — once the agent ends the call, tick the grace window
    // down, then run the completion sequence.
    useEffect(() => {
        if (!isConcluding) return;
        const id = window.setInterval(() => {
            setConcludingCountdown((s) => {
                if (s <= 1) {
                    window.clearInterval(id);
                    void handleCallEnd();
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConcluding]);

    // Full teardown on unmount — release camera + end the EL session.
    useEffect(() => {
        return () => {
            timersRef.current.forEach(clearTimeout);
            cancelAnimationFrame(drawRafRef.current);
            try {
                if (recorderRef.current && recorderRef.current.state !== "inactive") {
                    recorderRef.current.stop();
                }
            } catch {
                /* noop */
            }
            const conv = conversationRef.current;
            if (conv && typeof conv.endSession === "function") {
                Promise.resolve(conv.endSession()).catch(() => {});
            }
            cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
        };
    }, []);

    // ── Multipart recording → S3 ──────────────────────────────────────────────
    function startRecording() {
        const stream = cameraStreamRef.current;
        const cfg = uploadConfigRef.current;
        if (!stream || !cfg) return;

        const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
            ? "video/webm;codecs=vp8,opus"
            : "video/webm";

        // Record from a canvas we continuously paint the live preview into, instead
        // of straight from the camera track. A MediaRecorder is bound to the tracks
        // it starts with, so swapping cameraStreamRef on a camera change would never
        // reach the recording. Painting the *current* preview <video> into a canvas
        // and recording the canvas's captureStream means a mid-interview camera
        // switch (which just repoints the preview) IS captured. Falls back to the
        // raw stream if the browser lacks canvas.captureStream.
        const recordStream = buildCanvasRecordStream(stream) ?? stream;
        recordStreamRef.current = recordStream;

        const recorder = new MediaRecorder(recordStream, { mimeType });
        recorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
            if (!e.data || e.data.size === 0) return;
            bufferRef.current.push(e.data);
            bufferSizeRef.current += e.data.size;
            // Flush a part as soon as we've buffered the 5 MB minimum.
            if (bufferSizeRef.current >= MIN_PART_SIZE) {
                flushBufferedPart(mimeType);
            }
        };
        recorder.start(CHUNK_MS);
    }

    /** Build a recording stream whose video is a FIXED 16:9 canvas continuously
     *  painted from the live preview <video>, plus the current audio track. The
     *  camera image is letterboxed ("fit"/contain) onto a black background, so the
     *  recording shows the FULL frame at a consistent 16:9 shape (matching the v1
     *  recording) instead of cropping it — and a mid-interview camera switch (which
     *  repoints the preview) is still captured. Returns null if `canvas.captureStream`
     *  isn't available. */
    function buildCanvasRecordStream(stream: MediaStream): MediaStream | null {
        // Fixed 16:9 recording frame — consistent output shape + black background.
        const CANVAS_W = 1280;
        const CANVAS_H = 720;

        const canvas = document.createElement("canvas");
        if (typeof canvas.captureStream !== "function") return null;
        canvas.width = CANVAS_W;
        canvas.height = CANVAS_H;
        canvasRef.current = canvas;
        const ctx = canvas.getContext("2d");

        const draw = () => {
            const v = videoElRef.current;
            if (ctx) {
                // Black background (the letterbox / pillarbox padding).
                ctx.fillStyle = "#000";
                ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
                // drawImage reads the raw decoded frames — the preview's CSS mirror
                // does not apply, so the recording keeps a natural orientation.
                if (v && v.readyState >= 2 && v.videoWidth > 0 && v.videoHeight > 0) {
                    // "contain": scale to fit inside the frame preserving aspect,
                    // centered — the full camera image, never cropped.
                    const scale = Math.min(CANVAS_W / v.videoWidth, CANVAS_H / v.videoHeight);
                    const dw = v.videoWidth * scale;
                    const dh = v.videoHeight * scale;
                    const dx = (CANVAS_W - dw) / 2;
                    const dy = (CANVAS_H - dh) / 2;
                    ctx.drawImage(v, dx, dy, dw, dh);
                    drawWatermark(ctx, canvas);
                }
            }
            drawRafRef.current = requestAnimationFrame(draw);
        };
        drawRafRef.current = requestAnimationFrame(draw);

        const canvasStream = canvas.captureStream(30);
        const recordStream = new globalThis.MediaStream();
        canvasStream.getVideoTracks().forEach((t) => recordStream.addTrack(t));
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) recordStream.addTrack(audioTrack);
        return recordStream;
    }

    /** Paint the watermark small into the top-right corner of the recording canvas
     *  (the local-record equivalent of custom-voice-interview-v2's Daily watermark).
     *  Sized as a small fraction of the frame WIDTH using the image's NATURAL aspect
     *  ratio (so it's never stretched), at 0.9 opacity. No-op until the image loads. */
    function drawWatermark(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) {
        const wm = watermarkImgRef.current;
        if (!wm || !wm.complete || wm.naturalWidth === 0) return;
        const wmWidth = canvas.width * 0.12; // ~12% of frame width — small, top-right
        const wmHeight = wmWidth * (wm.naturalHeight / wm.naturalWidth);
        const margin = canvas.width * 0.025;
        const x = canvas.width - margin - wmWidth;
        const y = margin;
        const prevAlpha = ctx.globalAlpha;
        ctx.globalAlpha = 0.9;
        ctx.drawImage(wm, x, y, wmWidth, wmHeight);
        ctx.globalAlpha = prevAlpha;
    }

    /** Combine the buffered chunks into one ≥5MB part and enqueue its upload. */
    function flushBufferedPart(mimeType: string) {
        if (bufferRef.current.length === 0) return;
        const part = new globalThis.Blob(bufferRef.current, { type: mimeType });
        bufferRef.current = [];
        bufferSizeRef.current = 0;
        enqueuePartUpload(part);
    }

    /** Serialize uploads so PartNumber order is preserved. */
    function enqueuePartUpload(part: Blob) {
        const cfg = uploadConfigRef.current;
        if (!cfg) return;
        uploadChainRef.current = uploadChainRef.current.then(async () => {
            const partNumber = partNumberRef.current;
            // Retry the presign + PUT across a transient internet drop — the chunk
            // blob is captured here, so a dropped part is re-uploaded once the
            // connection recovers rather than lost (which would leave a gap that
            // fails the final CompleteMultipartUpload).
            const etag = await retryRecordingOp(
                async () => {
                    const presigned = await getElS3UploadPart({
                        upload_id: cfg.upload_id,
                        part_number: partNumber,
                        s3_key: cfg.s3_key,
                    });
                    if (!presigned) return null;
                    return await putElUploadPart(presigned, part);
                },
                (result) => !!result,
            );
            if (etag) {
                partsRef.current.push({ PartNumber: partNumber, ETag: etag });
                partNumberRef.current = partNumber + 1;
            }
        });
    }

    // ── FE-driven section timing ──────────────────────────────────────────────
    function startSectionTimers() {
        timersRef.current.forEach(clearTimeout);
        timersRef.current = [];

        let elapsed = 0;
        sections.forEach((section, idx) => {
            // Nudge the agent into the NEXT section at each boundary.
            elapsed += Math.max(0, Number(section.duration) || 0);
            const next = sections[idx + 1];
            if (next) {
                const timer = setTimeout(() => {
                    console.log(`[el-agent] section timer fired @${elapsed}s → nudge agent to Section ${idx + 2}: ${next.title}`);
                    conversation.sendContextualUpdate(
                        `Move to Section ${idx + 2}: ${next.title}. Transition now.`,
                    );
                }, elapsed * 1000);
                timersRef.current.push(timer);
            }
        });
        console.log(`[el-agent] startSectionTimers: ${sections.length} sections, maxDuration=${maxDuration}s`);

        // At the total budget, ask the agent to wrap up and end the call.
        const total = Math.max(0, Number(maxDuration) || 0);
        if (total > 0) {
            const wrap = setTimeout(() => {
                console.log(`[el-agent] wrap timer fired @${total}s → asking agent to wrap up + call end_call`);
                // Full closing script (handoff). The agent's prompt no longer carries the
                // ending instructions — the BE removed them so the agent can't end itself
                // early — so the entire closing must be delivered HERE, at maxDuration.
                // English is fine: it's an instruction the agent executes in the interview
                // language, like the "Move to Section N" notes above.
                conversation.sendContextualUpdate(
                    "The interview time is now up. Do NOT start any new topic or ask any further interview " +
                    "questions. Wrap up now, speaking in the interview language: " +
                    "First, briefly and naturally acknowledge the candidate's last answer in one sentence, without " +
                    "announcing that the interview is over. Then let them know we have reached the end and ask if " +
                    "they have any final questions or anything else they would like to add. WAIT for their reply. " +
                    "If they ask a question, answer it briefly (if you do not have the information, say so and " +
                    "suggest they reach out to the hiring team), then ask if there is anything else. If they add " +
                    "information, briefly thank them. Once they have nothing more, thank them once, give a brief " +
                    "warm closing, and then call the end_call tool to end the call. Say the final thank-you and " +
                    "goodbye only ONCE: if the candidate replies after it (for example 'thanks' or 'bye'), do not " +
                    "repeat it, just call end_call. Keep your delivery warm, professional, and natural.",
                );
            }, total * 1000);
            timersRef.current.push(wrap);

            // NOTE: no FE hard-stop. The session ends ONLY when the agent calls end_call
            // (→ onDisconnect reason="agent" → concluding countdown → handleCallEnd) or
            // the candidate presses End. The closing Q&A above is variable length, so a
            // fixed FE timer would cut it off mid-conversation.
        }
    }

    // Abort a failed session setup: surface an error, end the EL session, and
    // release the camera. erroredRef stops the disconnect handler from running
    // the completion sequence afterwards (so we never "complete" a broken setup).
    function failSetup(message: string) {
        erroredRef.current = true;
        setErrorMessage(message);
        setPhase("error");
        try {
            conversation.endSession();
        } catch {
            /* noop */
        }
        cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
    }

    // ── Start ─────────────────────────────────────────────────────────────────
    // Mirrors custom-voice-interview-v2's handleStartInterviewClick: validate the
    // media is ready, then show the confirmation modal. The actual session start
    // happens on confirm, in handleConfirmInterview.
    function handleStartInterviewClick() {
        if (!cameraReady) return;
        setShowConfirmationModal(true);
    }

    async function handleConfirmInterview() {
        if (startedRef.current) return;
        startedRef.current = true;
        setShowConfirmationModal(false);
        setPhase("starting");

        // Record that the candidate started the AI interview (real job only).
        if (jobId) {
            recordAIInterviewStarted(jobId).catch(() => {});
        }

        // 1. Create the DB interview record — REAL candidate only. HR test/preview
        //    (isTestMode) skips it: there's no candidate to persist, and
        //    /interviews/create isn't needed to run the agent session (it was also
        //    500-ing for the synthetic test context and blocking the preview), so the
        //    locally generated conversationId stands as-is there. For a real candidate
        //    the id below is only a proposal — the BE's response is authoritative.
        let conversationId = crypto.randomUUID();
        conversationIdRef.current = conversationId;
        if (!isTestMode) {
            const browser = getBrowserName();
            const created = await createInterview({
                conversation_id: conversationId,
                interview: "AI Voice Interview",
                assessment_id: assessmentId,
                browser_details: `${browser.name} (${browser.version})`,
                job_id: jobId ?? null,
            });
            // 202 ⇒ a record already exists for this conversation; both are fine to proceed.
            if (created.code && created.code >= 400) {
                setErrorMessage(created.message || (t(
                    "Could not start the interview.",
                    "Tidak dapat memulai wawancara.",
                    "Tidak dapat memulakan temu duga.",
                ) as string));
                setPhase("error");
                startedRef.current = false;
                return;
            }
            // The BE owns the conversation_id — on 201 and 202 alike it can hand
            // back an id that differs from ours (an abandoned attempt it couldn't
            // adopt, a retake, a second tab). Everything after this point is keyed
            // on it, including the S3 key the BE mints from the id we send to
            // /el/session below, so adopt it here: keeping our local id makes every
            // completion call 404 and loses the whole interview (#3460).
            conversationId = resolveConversationId(created, conversationId);
            conversationIdRef.current = conversationId;
        }

        // 2. Start the EL session + open the S3 multipart upload.
        const session = await startElSession({
            conversation_id: conversationId,
            assessment_id: assessmentId,
            candidate_name: candidateName,
            sections: sections.map((s) => ({ title: s.title, duration: s.duration })),
        });
        if (!session) {
            setErrorMessage(t(
                "This screening isn't configured for the v2 interview engine.",
                "Skrining ini belum dikonfigurasi untuk mesin wawancara v2.",
                "Saringan ini belum dikonfigurasikan untuk enjin temu duga v2.",
            ) as string);
            setPhase("error");
            startedRef.current = false;
            return;
        }
        uploadConfigRef.current = session.s3_upload_config;

        // 3. Connect the EL SDK (candidate_name fills {{candidate_name}} in the
        //    agent prompt + first message). onConnect handles recording/timers.
        try {
            conversation.startSession({
                signedUrl: session.el_signed_url,
                dynamicVariables: {
                    candidate_name: candidateName ?? "",
                    // Forward the candidate CV verbatim from the /session response so
                    // the shared agent can personalise its questions. The BE always
                    // returns a string ("No CV provided." when absent); default to
                    // that if an older BE omits the field.
                    candidate_cv: session.candidate_cv ?? "No CV provided.",
                    // Full job detail (JSON dump) so the agent can reference the
                    // role/company context. Dynamic variables must be primitives, so
                    // serialize; empty string when no job detail was provided.
                    job_application_detail: jobApplicationDetail != null
                        ? JSON.stringify(jobApplicationDetail)
                        : "",
                },
            });
        } catch {
            setErrorMessage(t(
                "Could not connect to the interview agent. Please try again.",
                "Tidak dapat terhubung ke agen wawancara. Silakan coba lagi.",
                "Tidak dapat menyambung ke ejen temu duga. Sila cuba lagi.",
            ) as string);
            setPhase("error");
            startedRef.current = false;
        }
    }

    // ── End ───────────────────────────────────────────────────────────────────
    // The End button doesn't end immediately — mirror custom-voice-interview-v2's
    // handleDialogConfirmEndInterview: if the interview isn't complete yet, show a
    // confirmation dialog ("just X more minutes…") so the candidate doesn't end by
    // accident. A "don't show again" cookie skips it on later ends, and a fully
    // elapsed interview ends straight away (no point prompting).
    function handleDialogConfirmEndInterview() {
        if (isEndingCall) return;
        const skipConfirm = getCookie("is-skip-confirm-end-interview") || false;
        const completionPercent =
            maxDuration > 0 ? (elapsedSeconds / maxDuration) * 100 : 100;
        if (skipConfirm || completionPercent >= 100) {
            void handleEnd();
            return;
        }
        setIsDialogConfirmEndInterview(true);
    }

    // Dialog "End anyway" — actually end, and remember the opt-out if it was ticked.
    function onHandleEndConfirmInterview(isDontShowAgain: boolean) {
        if (isDontShowAgain) {
            setCookie("is-skip-confirm-end-interview", "true", 7); // expires in 7 days
        }
        setIsDialogConfirmEndInterview(false);
        void handleEnd();
    }

    async function handleEnd() {
        if (isEndingCall) return;
        manualEndRef.current = true;
        setIsEndingCall(true);
        try {
            await conversation.endSession();
            // onDisconnect → handleCallEnd() runs the completion sequence.
        } catch {
            // If endSession throws, still run completion so we don't strand the upload.
            void handleCallEnd();
        }
    }

    /** Stop recording, flush the queue, finalize the upload, then mark complete. */
    async function handleCallEnd() {
        console.log(`[el-agent] handleCallEnd() invoked — manualEnd=${manualEndRef.current} isConcluding=${isConcluding} alreadyDone=${completedRef.current || erroredRef.current}`);
        if (completedRef.current || erroredRef.current) return;
        completedRef.current = true;
        setPhase("finishing");

        timersRef.current.forEach(clearTimeout);
        timersRef.current = [];

        // Stop the recorder and flush the tail (the final part may be < 5 MB).
        const recorder = recorderRef.current;
        const mimeType = recorder?.mimeType || "video/webm";
        if (recorder && recorder.state !== "inactive") {
            await new Promise<void>((resolve) => {
                recorder.onstop = () => resolve();
                try {
                    recorder.stop();
                } catch {
                    resolve();
                }
            });
        }
        // Stop the canvas paint loop + its captured video track now recording is done.
        cancelAnimationFrame(drawRafRef.current);
        recordStreamRef.current?.getVideoTracks().forEach((t) => t.stop());
        // Release the camera + mic NOW (mirrors custom-voice-interview-v2's
        // endConversation, which stops local media synchronously up front). The
        // recorder has already stopped so its data is fully captured — the device is
        // no longer needed, and the browser's camera/mic indicator turns off
        // immediately instead of staying on through the upload finalize below.
        cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
        flushBufferedPart(mimeType);
        // NOT awaited here. The upload chain is minutes of work on a long
        // interview, and everything below it — scoring, the submission status —
        // depends on the EL conversation id, not on a single byte of video. A
        // candidate who closes the tab while it runs used to lose the whole
        // interview (#3726); now they lose at most the recording.

        const convId = conversationIdRef.current;
        const durationSec = startTimeRef.current
            ? Math.round((Date.now() - startTimeRef.current) / 1000)
            : elapsedSeconds;

        // Status from how much of the interview elapsed (mirrors the 25%/50%
        // thresholds in custom-voice-interview-v2): under 25% of the target →
        // Aborted, 25–50% → Partial, over 50% → Complete.
        const targetDuration = maxDuration || 1200; // default 20 min
        const minDuration25Percent = targetDuration * 0.25;
        const minDuration50Percent = targetDuration * 0.5;
        let newStatus: "Aborted" | "Partial" | "Complete" = "Complete";
        if (durationSec < minDuration25Percent) {
            newStatus = "Aborted";
        } else if (durationSec >= minDuration25Percent && durationSec <= minDuration50Percent) {
            newStatus = "Partial";
        }
        console.log(`[el-agent] completion: status=${newStatus} durationSec=${durationSec} targetDuration=${targetDuration} (25%=${minDuration25Percent} 50%=${minDuration50Percent})`);

        // Candidate persistence — SKIPPED entirely for HR test/preview (isTestMode),
        // whose `userId` is a synthetic non-candidate id.
        //
        // ORDER IS THE FIX (#3726). This used to finalize the video upload first
        // and only then tell the backend the interview had happened. The comment
        // said a failed or missing upload must not block scoring; the code did
        // exactly that, because both calls sat behind `await uploadChainRef`.
        // A real candidate finished a 31-minute interview, closed the tab during
        // the saving spinner, and neither call was ever sent: no scoring, no
        // status, a drop-off reminder for an interview they had completed, and a
        // video that no longer existed anywhere.
        //
        // So the two calls that make the interview EXIST come first, off the EL
        // conversation id alone. The video is attached afterwards, to the same
        // record. Losing it costs a recording; losing the other two cost the
        // whole interview.
        if (!isTestMode) {
            // Mark the recording complete with retry, so the BE registers it once
            // the connection recovers — but stop dead on a 4xx (a missing record is
            // not a transient failure). No s3_key yet: the backend scores from the
            // stored EL transcript and only writes `videos.s3_link` when a key is
            // given, so this is the half that matters and it can go now.
            let saveFailed = false;
            if (convId) {
                const completed = await retryRecordingOp(
                    () => markElRecordingComplete(convId),
                    (r) => r.ok === true,
                    (r) => r.permanent === true,
                );
                if (!completed?.ok) {
                    saveFailed = true;
                    catchErrorLog(
                        `[el-agent] recording-complete failed permanent=${completed?.permanent} conversation_id=${convId}`,
                    );
                }
            }

            try {
                await updateInterviewStatusV2({
                    new_status: newStatus,
                    conversation_id: convId,
                    user_id: userId,
                });
            } catch (error) {
                // No retry on a 4xx (#3460) — updateInterviewStatusV2 throws the raw
                // axios error, so the status is available here.
                saveFailed = true;
                catchErrorLog(
                    `[el-agent] v2/update-status failed client_error=${isClientError(error)} conversation_id=${convId}: ${error}`,
                );
            }

            // The interview ran, but nothing was persisted — never show the candidate
            // a success screen for an interview we lost (#3460).
            if (saveFailed) {
                setSaveFailed(true);
                setErrorMessage(t(
                    "Your interview was recorded but we could not save it. Our team has been notified — please contact support before retaking it, so you don't have to sit the interview again.",
                    "Wawancara Anda terekam tetapi tidak dapat kami simpan. Tim kami telah diberi tahu — silakan hubungi dukungan sebelum mengulang, agar Anda tidak perlu mengikuti wawancara lagi.",
                    "Temu duga anda telah dirakam tetapi tidak dapat kami simpan. Pasukan kami telah dimaklumkan — sila hubungi sokongan sebelum mengulang, supaya anda tidak perlu menjalani temu duga sekali lagi.",
                ) as string);
                setPhase("error");
                return;
            }

            // Report the final status up so the parent can update the applicant's
            // assessment-completion status (mirrors custom-voice-interview-v2 calling
            // onInterviewStatusChange right after updateInterviewStatusV2 resolves).
            onInterviewStatusChange?.(newStatus);

            // ── The video, now that nothing depends on it ──────────────────────
            //
            // Everything above has landed: the interview is scored and the
            // submission reads Complete. This attaches the recording to that same
            // record, and is allowed to fail. It must NEVER set saveFailed — an
            // interview with results and no video is a small loss; showing the
            // candidate "we could not save it" over a missing recording would send
            // someone to retake an interview that is already finished.
            //
            // The tab is still on the saving spinner while this runs, and the
            // beforeunload guard is armed for it, so leaving is discouraged rather
            // than silently destructive.
            const cfg = uploadConfigRef.current;
            if (convId && cfg && partsRef.current.length > 0) {
                try {
                    await uploadChainRef.current;
                    const finalKey = await retryRecordingOp(
                        () => completeElS3Upload({
                            upload_id: cfg.upload_id,
                            s3_key: cfg.s3_key,
                            parts: partsRef.current,
                        }),
                        (key) => !!key,
                    );
                    if (finalKey) {
                        // Second call, same conversation. The BE writes the s3_link
                        // and leaves the status and insights it already computed
                        // alone, which is what makes calling it twice safe.
                        await retryRecordingOp(
                            () => markElRecordingComplete(convId, finalKey),
                            (r) => r.ok === true,
                            (r) => r.permanent === true,
                        );
                    } else {
                        catchErrorLog(
                            `[el-agent] video upload could not be finalized conversation_id=${convId} — interview saved without it`,
                        );
                    }
                } catch (error) {
                    catchErrorLog(
                        `[el-agent] video attach failed conversation_id=${convId} — interview saved without it: ${error}`,
                    );
                }
            }

            // NOTE: the completion email (POST /interviews/send-interview-feedback-email,
            // via sendInternalInterviewCompleteNotification) is intentionally NOT sent
            // here — it's triggered only when the candidate submits the FeedbackPopup
            // (with their actual rating/feedback), exactly like custom-voice-interview-v2,
            // which never calls it in its end pipeline.
        }

        setPhase("completed");
        // HR test/preview: stop on the test-complete screen (rendered below) so the
        // tester sees "Interview Complete" + tips; its "Return to Editor" button is
        // what calls onCloseModal. Don't auto-return or fire candidate callbacks.
        if (isTestMode) {
            return;
        }
        // Mirror custom-voice-interview-v2 exactly: call onCompletedInterview NOW for a
        // non-aborted run (it only updates the applicant's status — it does NOT advance
        // the UI; the parent advances via onCloseModal), then ALWAYS show the
        // FeedbackPopup (v1 sets status 'completed' regardless of Aborted, so HR/candidate
        // always gets the feedback form). The feedback's onComplete calls onCloseModal.
        if (newStatus !== "Aborted") {
            onCompletedInterview();
        }
        setShowFeedbackPopup(true);
    }

    const formattedTime = useMemo(() => {
        const m = Math.floor(elapsedSeconds / 60);
        const s = elapsedSeconds % 60;
        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }, [elapsedSeconds]);

    const joinCountdownLabel = useMemo(() => {
        const m = Math.floor(joinCountdown / 60);
        const s = joinCountdown % 60;
        return `${m}:${String(s).padStart(2, "0")}`;
    }, [joinCountdown]);

    const aiDisplayName = aiName || "ourteam AI";

    // Setup-checks "Network" row — derived from the Network Information API signals
    // above (mirrors custom-voice-interview-v2): offline → error, slow → warning
    // (advisory), else excellent. The downlink number is suppressed below 1 Mbps
    // because the API often reports misleadingly low values on healthy links.
    const networkState: "ok" | "warning" | "error" = !isOnline
        ? "error"
        : connectionQuality === "good"
            ? "ok"
            : "warning";
    const networkTitle =
        networkState === "error"
            ? t("No internet", "Tidak ada internet", "Tiada internet")
            : networkState === "warning"
                ? t("Network okay", "Jaringan cukup baik", "Rangkaian okay")
                : t("Network excellent", "Jaringan sangat baik", "Rangkaian sangat baik");
    const networkSubtitle = (() => {
        if (networkState === "error") {
            return t(
                "Check your internet connection.",
                "Periksa koneksi internet Anda.",
                "Periksa sambungan internet anda.",
            );
        }
        const mbps = downlink != null && downlink >= 1 ? `${downlink.toFixed(1)} Mbps` : null;
        if (networkState === "warning") {
            const hint = t(
                "Try a stronger signal spot.",
                "Coba cari tempat dengan sinyal lebih kuat.",
                "Cuba cari tempat dengan isyarat lebih kuat.",
            ) as string;
            return mbps ? `${mbps}. ${hint}` : hint;
        }
        const latency =
            rtt != null
                ? (rtt < 100
                    ? (t("low latency", "latensi rendah", "latensi rendah") as string)
                    : (t("high latency", "latensi tinggi", "latensi tinggi") as string))
                : null;
        if (mbps && latency) return `${mbps}, ${latency}`;
        if (mbps) return mbps;
        return t("You're online.", "Anda sedang online.", "Anda dalam talian.");
    })();

    // Current device names for the waiting-room selector buttons (label = the
    // selected device, mirroring custom-voice-interview-v2's device selectors).
    const audioInputName = useMemo(
        () =>
            audioInputs.find((d) => d.deviceId === selectedMicId)?.label ||
            (t("Default microphone", "Mikrofon bawaan", "Mikrofon lalai") as string),
        [audioInputs, selectedMicId, language],
    );
    const audioOutputName = useMemo(
        () =>
            audioOutputs.find((d) => d.deviceId === selectedSpeakerId)?.label ||
            (t("Default speaker", "Speaker bawaan", "Pembesar suara lalai") as string),
        [audioOutputs, selectedSpeakerId, language],
    );
    const videoName = useMemo(
        () =>
            videoInputs.find((d) => d.deviceId === selectedCameraId)?.label ||
            (t("Default camera", "Kamera bawaan", "Kamera lalai") as string),
        [videoInputs, selectedCameraId, language],
    );

    // Adapt the internal transcript → TranscriptionPanel.Message shape.
    // Only the AI's questions/prompts are shown — the candidate's own answers
    // are intentionally NOT rendered in the live transcript panel.
    const transcriptionMessages = useMemo(
        () =>
            transcript
                .filter((m) => m.source === "ai")
                .map((m, idx) => ({
                    id: `${idx}`,
                    role: m.source,
                    content: m.message,
                    timestamp: new Date(),
                    isComplete: true,
                })),
        [transcript],
    );

    // ── Error ─────────────────────────────────────────────────────────────────
    if (phase === "error") {
        return (
            <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center gap-4 p-6 text-center">
                <p className="text-base text-gray-800 max-w-md">{errorMessage}</p>
                {!saveFailed && (
                    <Button onClick={() => window.location.reload()}>{t("Reload", "Muat ulang", "Muat semula")}</Button>
                )}
            </div>
        );
    }

    // ── Finishing / completed ─────────────────────────────────────────────────
    if (phase === "finishing" || phase === "completed") {
        // HR test/preview: once finished, show the test-complete screen (tips +
        // Return to Editor) instead of the candidate "saving recording" spinner.
        // Mirrors the v1 voice/video container test screen. "Return to Editor"
        // returns via onCloseModal (= handleBackFromTest on the employer pages).
        if (isTestMode && phase === "completed") {
            return (
                <div className="fixed inset-0 z-50 bg-white flex items-center justify-center p-6">
                    <div className="w-full max-w-md text-center">
                        <div className="inline-flex items-center justify-center p-3 bg-green-100 rounded-full mb-4">
                            <CheckCircle2Icon className="h-10 w-10 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold mb-4 text-gray-900">{t("Interview Complete", "Wawancara Selesai", "Temu Duga Selesai")}</h2>
                        <div className="space-y-3">
                            <Button
                                onClick={() => onCloseModal?.()}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                {t("Return to Editor", "Kembali ke Editor", "Kembali ke Editor")}
                            </Button>
                            <div className="flex flex-col gap-2 text-sm text-left p-4 bg-blue-50 rounded-lg">
                                <div className="flex items-center gap-2 text-blue-700">
                                    <InfoIcon className="h-5 w-5" />
                                    <h3 className="font-medium text-blue-700">{t("Testing Tips:", "Tips Pengujian:", "Petua Ujian:")}</h3>
                                </div>
                                <ul className="list-disc list-inside text-gray-700 space-y-1">
                                    <li>{t("Try testing with different persona profiles.", "Coba uji dengan profil persona yang berbeda.", "Cuba uji dengan profil persona yang berbeza.")}</li>
                                    <li>{t("Ensure all questions are clear and understandable.", "Pastikan semua pertanyaan jelas dan mudah dipahami.", "Pastikan semua soalan jelas dan mudah difahami.")}</li>
                                    <li>{t("Verify transitions between sections flow naturally.", "Periksa transisi antar bagian mengalir secara alami.", "Sahkan peralihan antara bahagian mengalir secara semula jadi.")}</li>
                                    <li>{t("Check that timing allocations feel appropriate.", "Periksa alokasi waktu terasa sesuai.", "Periksa peruntukan masa terasa sesuai.")}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        // Real candidate, any completed run (Complete/Partial/Aborted): collect
        // post-interview feedback — mirrors the v1 VideoContainer FeedbackPopup, shown
        // for every ended run. Submitting (or skipping) advances the parent via
        // onCloseModal (onCompletedInterview already fired in handleCallEnd).
        if (phase === "completed" && showFeedbackPopup) {
            return (
                <div className="fixed inset-0 z-50 bg-white flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
                    <FeedbackPopup
                        interviewName={interviewName || aiDisplayName}
                        assessmentId={assessmentId}
                        userId={userId}
                        conversationId={conversationIdRef.current}
                        interviewStartTime={startTimeRef.current}
                        interviewLanguage={language}
                        selectedAudioDeviceID={selectedMicId || "default"}
                        selectedVideoDeviceID={selectedCameraId || "default"}
                        hasFormAssessment={hasFormAssessment}
                        hasEligibilityCheck={hasEligibilityCheck}
                        onComplete={() => onCloseModal?.()}
                    />
                </div>
            );
        }
        return (
            <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center gap-4 p-6 text-center">
                <Loader2Icon className="h-8 w-8 animate-spin text-blue-600" />
                <p className="text-base text-gray-800">
                    {t(
                        "Wrapping up your interview and saving the recording…",
                        "Menyelesaikan wawancara Anda dan menyimpan rekaman…",
                        "Menyelesaikan temu duga anda dan menyimpan rakaman…",
                    )}
                </p>
            </div>
        );
    }

    // ── Waiting room ──────────────────────────────────────────────────────────
    // Mirrors the InterviewVoiceOnlyV1 "get ready" setup screen: a header (title +
    // duration + description), a left preview (the candidate camera, since v2
    // records video — the only deviation from the voice-only Blob), a static
    // audio-levels card, and a right column with the readiness tips, a setup-checks
    // card, and the Join / "having issues" actions. No backend call here.
    if (phase === "waiting" || phase === "starting") {
        const joining = phase === "starting";
        const durationMins = Math.round((maxDuration ?? 0) / 60);
        const durationLabel = t(`${durationMins} min`, `${durationMins} menit`, `${durationMins} minit`);
        return (
            <div className="w-full max-w-6xl mx-auto px-0 py-0 sm:px-4 sm:py-6" id="el-waiting-room">
                {/* Header */}
                <div className="flex flex-col">
                    <h1 className="text-xl sm:text-3xl font-semibold sm:font-bold text-gray-900 dark:text-white mb-2">
                        {interviewName || t("AI Voice Interview", "Wawancara Suara AI", "Temu Duga Suara AI")}
                    </h1>
                    <div className="text-gray-600 dark:text-gray-400 mb-6 flex flex-row items-center gap-2">
                        <span className="flex-shrink-0 text-sm font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full">
                            {durationLabel}
                        </span>
                        <p className="md:ml-2 text-sm">
                            {description || t(
                                `${aiDisplayName} will interview you. When you're ready, join to begin.`,
                                `${aiDisplayName} akan mewawancarai Anda. Saat Anda siap, gabung untuk memulai.`,
                                `${aiDisplayName} akan menemu duga anda. Apabila anda bersedia, sertai untuk bermula.`,
                            )}
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-[1.2fr,1fr] gap-8">
                    {/* Left — camera preview + device selectors + live audio level */}
                    <div className="flex flex-col gap-2">
                        <div className="rounded-xl border text-card-foreground shadow overflow-hidden h-[400px] bg-gray-100 dark:bg-gray-800 relative flex items-center justify-center">
                            <video
                                ref={videoElRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full h-full object-cover -scale-x-100"
                            />
                            {!camGranted && (
                                <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm gap-2">
                                    <VideoIcon className="h-4 w-4" /> {t("Waiting for camera…", "Menunggu kamera…", "Menunggu kamera…")}
                                </div>
                            )}

                            {/* Mic + Camera status badges (top-right) — per-device, so
                                a partial grant shows the correct state for each. */}
                            <div className="absolute flex flex-row top-3 right-3 z-20 space-x-2">
                                <div
                                    className={`flex items-center gap-1.5 px-2.5 py-1 opacity-90 rounded-full text-xs font-medium transition-colors backdrop-blur-sm ${micGranted ? "bg-green-600/70 text-white" : "bg-red-600/70 text-white"}`}
                                    title={micGranted
                                        ? (t("Microphone is active", "Mikrofon aktif", "Mikrofon aktif") as string)
                                        : (t("Microphone access needed", "Akses mikrofon diperlukan", "Akses mikrofon diperlukan") as string)}
                                >
                                    {micGranted ? (
                                        <MicrophoneIcon className="h-3.5 w-3.5" />
                                    ) : (
                                        <MicOffIcon className="h-3.5 w-3.5" />
                                    )}
                                    <span>{micGranted ? t("Mic On", "Mik Aktif", "Mik Aktif") : t("Mic Off", "Mik Mati", "Mik Mati")}</span>
                                </div>
                                <div
                                    className={`flex items-center gap-1.5 px-2.5 py-1 opacity-90 rounded-full text-xs font-medium transition-colors backdrop-blur-sm ${camGranted ? "bg-green-600/70 text-white" : "bg-red-600/70 text-white"}`}
                                    title={camGranted
                                        ? (t("Camera is active", "Kamera aktif", "Kamera aktif") as string)
                                        : (t("Camera access needed", "Akses kamera diperlukan", "Akses kamera diperlukan") as string)}
                                >
                                    {camGranted ? (
                                        <VideoIcon className="h-3.5 w-3.5" />
                                    ) : (
                                        <VideoOffIcon className="h-3.5 w-3.5" />
                                    )}
                                    <span>{camGranted ? t("Camera On", "Kamera Aktif", "Kamera Aktif") : t("Camera Off", "Kamera Mati", "Kamera Mati")}</span>
                                </div>
                            </div>

                            <span className="absolute bottom-3 left-3 text-sm font-medium text-white bg-black/60 px-3 py-1.5 rounded-lg">
                                You
                            </span>
                        </div>

                        {/* Device selectors — Microphone / Speaker / Camera, matching
                            custom-voice-interview-v2 (icon + device name + chevron,
                            opening a dropdown of devices). */}
                        <div className="grid grid-cols-3 gap-2">
                            {/* Microphone */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild disabled={audioInputs.length === 0}>
                                    <div>
                                        <MicrophoneSelector label={audioInputName} className="text-sm w-full" />
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" side="bottom" className="w-[420px] p-0">
                                    <div className="p-2">
                                        {audioInputs.map((device) => {
                                            const isSelected = selectedMicId === device.deviceId;
                                            return (
                                                <DropdownMenuItem
                                                    key={device.deviceId}
                                                    onClick={(e) => { e.preventDefault(); selectMic(device.deviceId); }}
                                                    className="cursor-pointer text-sm"
                                                >
                                                    {isSelected && <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />}
                                                    <span className={cn("flex-1", isSelected ? "text-blue-600 dark:text-blue-400 font-medium" : "text-foreground")}>
                                                        {device.label || `Microphone ${device.deviceId.slice(0, 5)}`}
                                                    </span>
                                                </DropdownMenuItem>
                                            );
                                        })}
                                        {audioInputs.length === 0 && <DropdownMenuItem disabled>{t("No microphones found", "Mikrofon tidak ditemukan", "Mikrofon tidak dijumpai")}</DropdownMenuItem>}
                                    </div>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Speaker */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild disabled={audioOutputs.length === 0}>
                                    <div>
                                        <SpeakerSelector label={audioOutputName} className="text-sm w-full" />
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" side="bottom" className="w-[420px] p-0">
                                    <div className="p-2">
                                        {audioOutputs.map((device) => {
                                            const isSelected = selectedSpeakerId === device.deviceId;
                                            return (
                                                <DropdownMenuItem
                                                    key={device.deviceId}
                                                    onClick={(e) => { e.preventDefault(); setSelectedSpeakerId(device.deviceId); }}
                                                    className="cursor-pointer text-sm"
                                                >
                                                    {isSelected && <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />}
                                                    <span className={cn("flex-1", isSelected ? "text-blue-600 dark:text-blue-400 font-medium" : "text-foreground")}>
                                                        {device.label || `Speaker ${device.deviceId.slice(0, 5)}`}
                                                    </span>
                                                </DropdownMenuItem>
                                            );
                                        })}
                                        {audioOutputs.length === 0 && <DropdownMenuItem disabled>{t("No speakers found", "Speaker tidak ditemukan", "Pembesar suara tidak dijumpai")}</DropdownMenuItem>}
                                    </div>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Camera */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild disabled={videoInputs.length === 0 || cameraSwitching}>
                                    <div className={cn({ "pointer-events-none opacity-50": cameraSwitching })}>
                                        <CameraSelector label={videoName} className="text-sm w-full" />
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" side="bottom" className="w-[420px] p-0">
                                    <div className="p-2">
                                        {videoInputs.map((device) => {
                                            const isSelected = selectedCameraId === device.deviceId;
                                            return (
                                                <DropdownMenuItem
                                                    key={device.deviceId}
                                                    onClick={(e) => { e.preventDefault(); selectCamera(device.deviceId); }}
                                                    className="cursor-pointer text-sm"
                                                >
                                                    {isSelected && <CheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />}
                                                    <span className={cn("flex-1", isSelected ? "text-blue-600 dark:text-blue-400 font-medium" : "text-foreground")}>
                                                        {device.label || `Camera ${device.deviceId.slice(0, 5)}`}
                                                    </span>
                                                </DropdownMenuItem>
                                            );
                                        })}
                                        {videoInputs.length === 0 && <DropdownMenuItem disabled>{t("No cameras found", "Kamera tidak ditemukan", "Kamera tidak dijumpai")}</DropdownMenuItem>}
                                    </div>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>

                        {/* Live audio level meter (real — its own mic capture), in a
                            card to match custom-voice-interview-v2. */}
                        <div className="rounded-lg border border-border bg-card">
                            <AudioLevelIndicator deviceId={selectedMicId || undefined} />
                        </div>
                    </div>

                    {/* Right — get ready, readiness bullets, setup checks, actions */}
                    <div className="flex flex-col gap-6 items-center justify-center w-full mb-24 sm:mb-0">
                        <div className="w-full sm:w-[440px]">
                            <h2 className="text-2xl md:text-xl font-semibold mt-0 sm:mt-4 md:mt-0 mb-4">
                                {t("Get ready for your AI interview", "Bersiaplah untuk wawancara AI Anda", "Bersedia untuk temu duga AI anda")}
                            </h2>

                            <div className="space-y-4 mb-6">
                                <div className="flex items-center gap-3">
                                    <StarIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                        {t("Start now or come back later", "Mulai sekarang atau kembali nanti", "Mula sekarang atau kembali kemudian")}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <TrendingUpIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                        {t("Stand out beyond a CV and get noticed faster", "Tampil lebih dari sekadar CV dan lebih cepat diperhatikan", "Serlah melebihi CV dan lebih cepat diperhatikan")}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <SettingsIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                        {t("Check your camera, microphone, and internet connection", "Periksa kamera, mikrofon, dan koneksi internet Anda", "Periksa kamera, mikrofon, dan sambungan internet anda")}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <WifiIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                        {t("Find a quiet place with stable internet", "Cari tempat yang tenang dengan internet stabil", "Cari tempat yang tenang dengan internet stabil")}
                                    </span>
                                </div>
                            </div>

                            {/* Action area */}
                            <div className="w-full space-y-3 sm:space-y-2 flex flex-col items-center mt-4 sm:mt-0 gap-2">
                                {/* Setup checks — mic/camera reflect real PER-DEVICE
                                    access (camGranted / micGranted), so a partial grant
                                    shows the granted one green and the other red + a
                                    "to fix" badge and an Enable button, like v1. */}
                                <SetupChecksPanel
                                    title={t("Setup checks", "Pemeriksaan persiapan", "Pemeriksaan persediaan")}
                                    rows={[
                                        {
                                            key: "mic",
                                            ok: micGranted,
                                            title: micGranted
                                                ? t("Microphone enabled", "Mikrofon aktif", "Mikrofon diaktifkan")
                                                : t("Microphone access needed", "Akses mikrofon diperlukan", "Akses mikrofon diperlukan"),
                                            subtitle: micGranted
                                                ? t("We can hear you clearly.", "Kami dapat mendengar Anda dengan jelas.", "Kami boleh mendengar anda dengan jelas.")
                                                : t("Allow microphone access to continue.", "Izinkan akses mikrofon untuk melanjutkan.", "Benarkan akses mikrofon untuk meneruskan."),
                                            onEnable: micGranted ? undefined : () => void acquireMedia(),
                                            enableLabel: t("Enable", "Aktifkan", "Aktifkan"),
                                        },
                                        {
                                            key: "camera",
                                            ok: camGranted,
                                            title: camGranted
                                                ? t("Camera enabled", "Kamera aktif", "Kamera diaktifkan")
                                                : t("Camera access needed", "Akses kamera diperlukan", "Akses kamera diperlukan"),
                                            subtitle: camGranted
                                                ? t("We can see you clearly.", "Kami dapat melihat Anda dengan jelas.", "Kami boleh melihat anda dengan jelas.")
                                                : t("Allow camera access to continue.", "Izinkan akses kamera untuk melanjutkan.", "Benarkan akses kamera untuk meneruskan."),
                                            onEnable: camGranted ? undefined : () => void acquireMedia(),
                                            enableLabel: t("Enable", "Aktifkan", "Aktifkan"),
                                        },
                                        {
                                            key: "network",
                                            ok: networkState === "ok",
                                            variant: networkState === "warning" ? "warning" : "error",
                                            title: networkTitle,
                                            subtitle: networkSubtitle,
                                        },
                                    ]}
                                    timeToJoinElement={joinCountdownLabel}
                                />

                                {!isBrowserSupported && (
                                    <p className="w-full text-xs text-amber-700 dark:text-amber-400 text-center">
                                        {t(
                                            "Your browser may not be supported. For the best experience, use Chrome, Firefox, Safari, or Edge.",
                                            "Browser Anda mungkin tidak didukung. Untuk pengalaman terbaik, gunakan Chrome, Firefox, Safari, atau Edge.",
                                            "Pelayar anda mungkin tidak disokong. Untuk pengalaman terbaik, gunakan Chrome, Firefox, Safari, atau Edge.",
                                        )}
                                    </p>
                                )}

                                <Button
                                    onClick={handleStartInterviewClick}
                                    disabled={!cameraReady || joining || !isBrowserSupported || !isOnline}
                                    className="w-full text-white py-[21px] rounded-full bg-blue-600 hover:bg-blue-700 cursor-pointer inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {joining ? (
                                        <>
                                            <Loader2Icon className="h-5 w-5 animate-spin" /> {t("Starting…", "Memulai…", "Memulakan…")}
                                        </>
                                    ) : (!cameraReady || !isOnline) ? (
                                        // A blocking setup-check item is unmet — tell the
                                        // candidate to resolve it (mirrors custom-v2).
                                        t("Resolve the issues above to start", "Atasi masalah di atas untuk mulai", "Selesaikan isu di atas untuk mula")
                                    ) : (
                                        t("Start Interview", "Mulai wawancara", "Mula Temu Duga")
                                    )}
                                </Button>

                                <Button
                                    variant="outline"
                                    onClick={() => setIsIssuesDialogOpen(true)}
                                    className="w-full bg-transparent border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full py-[21px]"
                                >
                                    {t("I'm having issues", "Saya mengalami masalah", "Saya menghadapi masalah")}
                                </Button>

                                <p className="w-full self-start text-xs text-gray-500 dark:text-gray-400 text-left leading-relaxed">
                                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                                        {t("Privacy:", "Privasi:", "Privasi:")}
                                    </span>{" "}
                                    {t(
                                        "Your responses are only used to evaluate your candidacy.",
                                        "Jawaban Anda hanya digunakan untuk menilai pencalonan Anda.",
                                        "Jawapan anda hanya digunakan untuk menilai pencalonan anda.",
                                    )}
                                    <br />
                                    {t(
                                        "They are not used to train AI models.",
                                        "Jawaban tersebut tidak digunakan untuk melatih model AI.",
                                        "Ia tidak digunakan untuk melatih model AI.",
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <IssuesDialog
                    isOpen={isIssuesDialogOpen}
                    onClose={() => setIsIssuesDialogOpen(false)}
                    onSubmit={() => setIsIssuesDialogOpen(false)}
                    userId={userId}
                    assessmentId={assessmentId}
                />

                {showConfirmationModal && (
                    <InterviewConfirmationModal
                        onClose={() => setShowConfirmationModal(false)}
                        onConfirm={handleConfirmInterview}
                        loading={joining}
                        interviewLanguage={language}
                        colorScheme="default"
                    />
                )}
            </div>
        );
    }

    // ── Live ──────────────────────────────────────────────────────────────────
    // Mirrors the VideoContainer / InterviewVoiceOnlyActive live-call layout:
    // the candidate camera fills the main frame (the recorded stream), an AI
    // persona tile floats as PiP, a live status bar shows whose turn it is, the
    // controls footer holds the timer + title + settings + end + transcript
    // toggle, and the transcript slides in on the right. UI only — the EL session,
    // recording, timers, and completion logic above are untouched.
    const liveState = (conversation.isSpeaking
        ? "ai-speaking"
        : conversation.isListening
            ? "user-turn"
            : null) as LiveStatusState;

    return (
        <div className="fixed inset-0 z-50 bg-black" id="el-live-session-room">
            <div
                id="ai-interview-container"
                className="w-full h-full bg-black overflow-hidden flex flex-col"
            >
                {/* Concluding banner — shown while the grace countdown runs after
                    the agent ends the call. */}
                {isConcluding && (
                    <div className="py-2 px-3 sm:px-4 flex items-center justify-between gap-2 text-xs sm:text-base bg-blue-600 text-white">
                        <div className="flex items-center gap-2 min-w-0">
                            <ClockIcon className="h-4 w-4 flex-shrink-0" />
                            <span className="font-medium truncate">{t("Interview concluded", "Wawancara selesai", "Temu duga selesai")}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="hidden sm:inline">{t("Finalizing in:", "Menyelesaikan dalam:", "Memuktamadkan dalam:")}</span>
                            <span className="sm:hidden">{t("Closing in:", "Menutup dalam:", "Menutup dalam:")}</span>
                            <span className="font-bold text-lg sm:text-xl tabular-nums">{concludingCountdown}</span>
                        </div>
                    </div>
                )}

                {/* Main content — flex row */}
                <div className="flex-1 flex flex-row min-h-0 relative">
                    {/* Left column: candidate video + controls footer. min-h-0 lets
                        it shrink so the controls footer stays on-screen on mobile. */}
                    <div className="flex flex-col flex-1 min-h-0">
                        {/* Candidate camera (the recorded stream) fills the frame,
                            mirrored for a natural self-view (display only). The video
                            is wrapped absolute-inset-0 (like VideoContainer) so its
                            intrinsic height never grows the column past the viewport
                            and pushes the controls footer below the fold on mobile. */}
                        <div className="relative flex-1 overflow-hidden bg-black">
                            <div className="absolute inset-0">
                                <video
                                    ref={videoElRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    // object-contain (letterboxed on black) so the
                                    // live view shows the FULL frame the recording
                                    // captures, not a cropped object-cover fill.
                                    className="w-full h-full object-contain -scale-x-100"
                                />
                            </div>

                            {/* Live status banner — "AI speaking" / "your turn". */}
                            <LiveStatusBar state={liveState} aiName={aiDisplayName} />

                            {/* "You" label with live audio visualizer (when EL is
                                listening — i.e. the candidate's turn). */}
                            <div className="absolute bottom-2 left-2">
                                <div
                                    className={`text-sm font-medium text-white bg-black/60 px-3 py-1.5 rounded-lg flex flex-row items-center ${conversation.isListening ? "gap-2" : ""}`}
                                >
                                    You
                                    {conversation.isListening && (
                                        <EnhancedAudioVisualizer
                                            isActive={true}
                                            variant="bars"
                                            color="green"
                                            getVolume={getInputVolume}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* AI persona tile (PiP) — speaking state from EL. */}
                            <AIPersonaOverlay
                                name={aiDisplayName}
                                imageSrc={""}
                                isSpeaking={conversation.isSpeaking}
                                className="absolute bottom-4 right-4 w-32 h-24 md:w-48 md:h-36"
                                primaryColor="blue"
                            />
                        </div>

                        {/* Controls Footer */}
                        <div className="bg-gray-900 text-white p-3 flex justify-between items-center flex-shrink-0">
                            {/* Left — timer + title */}
                            <div className="flex-1 flex items-center gap-4 min-w-0">
                                <span className="text-sm font-mono text-gray-300 inline-flex items-center flex-shrink-0">
                                    <ClockIcon className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                                    {formattedTime}
                                </span>
                                {interviewName && (
                                    <>
                                        <div className="border-l border-gray-600 h-5 flex-shrink-0" />
                                        <div className="truncate text-sm min-w-0 max-w-[120px] sm:max-w-none" title={interviewName}>
                                            {interviewName}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Center — Settings + End + Report (flex-shrink-0 so they
                                always stay on-screen, never squeezed off by the
                                timer/title on narrow mobile widths). */}
                            <div className="flex-1 flex items-center justify-center gap-2 md:gap-3 flex-shrink-0">
                                <button
                                    onClick={() => setShowSettings(true)}
                                    title={t("Device settings", "Pengaturan perangkat", "Tetapan peranti") as string}
                                    aria-label={t("Device settings", "Pengaturan perangkat", "Tetapan peranti") as string}
                                    className="p-2 md:p-3 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                                >
                                    <SettingsIcon className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={handleDialogConfirmEndInterview}
                                    disabled={isEndingCall}
                                    title={(isEndingCall
                                        ? t("Ending session…", "Mengakhiri sesi…", "Menamatkan sesi…")
                                        : t("End", "Akhiri", "Tamatkan")) as string}
                                    aria-label={t("End session", "Akhiri sesi", "Tamatkan sesi") as string}
                                    aria-busy={isEndingCall}
                                    className="p-2 md:p-3 rounded-full bg-red-500 hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isEndingCall ? (
                                        <Loader2Icon className="h-5 w-5 animate-spin" />
                                    ) : (
                                        <PhoneIcon className="h-5 w-5" />
                                    )}
                                </button>
                                <button
                                    onClick={() => setIsIssuesDialogOpen(true)}
                                    title={t("Report issue", "Laporkan masalah", "Laporkan isu") as string}
                                    aria-label={t("Report issue", "Laporkan masalah", "Laporkan isu") as string}
                                    className="p-2 md:p-3 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                                >
                                    <AlertTriangleIcon className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Right — transcript toggle (desktop only) */}
                            <div className="hidden sm:flex flex-1 items-center gap-3 w-auto justify-end pl-2">
                                <TranscriptionToggleButton
                                    isOpen={showTranscript}
                                    onClick={() => setShowTranscript((v) => !v)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right column: Transcription Panel (kept mounted across toggles
                        via `hidden` so its typing animation state is preserved). */}
                    <div className={showTranscript ? "h-full" : "hidden"}>
                        <TranscriptionPanel
                            messages={transcriptionMessages}
                            aiName={aiDisplayName}
                            aiSpeaking={conversation.isSpeaking}
                            userSpeaking={conversation.isListening}
                            aiThinking={false}
                            className="border-l border-gray-700 h-full"
                            onClose={() => setShowTranscript(false)}
                            hasFormAssessment={hasFormAssessment}
                            hasEligibilityCheck={hasEligibilityCheck}
                        />
                    </div>
                </div>
            </div>

            <SettingsModal
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                onSave={({ camera, microphone, speaker }) => {
                    if (camera) selectCamera(camera);
                    if (microphone) {
                        setSelectedMicId(microphone);
                        conversation.changeInputDevice({ inputDeviceId: microphone }).catch(() => {});
                    }
                    if (speaker) {
                        setSelectedSpeakerId(speaker);
                        conversation.changeOutputDevice({ outputDeviceId: speaker }).catch(() => {});
                    }
                    setShowSettings(false);
                }}
                initialSelectedCamera={selectedCameraId}
                initialSelectedMicrophone={selectedMicId}
                initialSelectedSpeaker={selectedSpeakerId}
            />

            {/* Report-issue dialog — opened from the live footer's report button. */}
            <IssuesDialog
                isOpen={isIssuesDialogOpen}
                onClose={() => setIsIssuesDialogOpen(false)}
                onSubmit={() => setIsIssuesDialogOpen(false)}
                userId={userId}
                assessmentId={assessmentId}
            />

            {/* Confirm-before-end dialog — shown when the candidate taps End before
                the interview is complete (mirrors custom-voice-interview-v2). */}
            {isDialogConfirmEndInterview && (
                <InterviewEndConfirmDialogV2
                    isOpen={isDialogConfirmEndInterview}
                    isTestMode={isTestMode}
                    isEarlyExit={maxDuration > 0 && (elapsedSeconds / maxDuration) * 100 >= 51}
                    remainingMinutes={Math.ceil((maxDuration - elapsedSeconds) / 60)}
                    onClose={() => setIsDialogConfirmEndInterview(false)}
                    onConfirm={onHandleEndConfirmInterview}
                />
            )}
        </div>
    );
}

/** Persist the EL conversation id, retrying a few times on transient failure.
 *  Returns true once the BE has stored the mapping, false if every attempt failed. */
/** Resolve once the browser reports it's back online, or after `maxWaitMs` — so a
 *  recording-finalize retry fires when the connection actually recovers instead of
 *  burning attempts while still offline. */
function waitForOnline(maxWaitMs: number): Promise<void> {
    if (typeof navigator === "undefined" || navigator.onLine) return Promise.resolve();
    return new Promise<void>((resolve) => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            window.removeEventListener("online", finish);
            clearTimeout(timer);
            resolve();
        };
        const timer = setTimeout(finish, maxWaitMs);
        window.addEventListener("online", finish);
    });
}

/** Retry a recording-finalize op (S3 part upload / complete / mark-complete) across
 *  an internet drop so the recording still gets saved. The KEY guarantee: while the
 *  connection is DOWN we keep waiting (re-checking every ~2s, and waking instantly on
 *  the browser's `online` event) WITHOUT consuming the retry budget — so the moment
 *  the connection is back to normal we attempt again, ensuring the session-end call
 *  (e.g. interviews/el/s3-complete) eventually fires. Only failures that happen while
 *  ONLINE count against `maxOnlineAttempts` (so a genuinely broken server doesn't loop
 *  forever). Each attempt is timeout-bounded so a stuck request can't hang completion.
 *  `isSuccess` decides whether a returned value counts as done (these services swallow
 *  errors and return a falsy value rather than throwing). */
async function retryRecordingOp<T>(
    op: () => Promise<T>,
    isSuccess: (result: T) => boolean,
    isPermanentFailure: (result: T) => boolean = () => false,
    maxOnlineAttempts = 10,
): Promise<T | null> {
    const RETRY_INTERVAL_MS = 2000; // retry cadence — every 2s
    const MAX_OFFLINE_WAIT_MS = 10 * 60 * 1000; // safety cap: stop after 10 min fully offline
    const isOnline = () => typeof navigator === "undefined" || navigator.onLine;

    let last: T | null = null;
    let onlineAttempts = 0;
    let offlineWaited = 0;

    while (onlineAttempts < maxOnlineAttempts) {
        // Offline → don't attempt (it would just fail). Wait for the connection to
        // return: wake on the `online` event, or re-check every 2s. This does NOT
        // consume the online-attempt budget, so we retry indefinitely across a drop
        // (bounded only by MAX_OFFLINE_WAIT_MS so a permanently-offline tab can't spin
        // forever).
        if (!isOnline()) {
            if (offlineWaited >= MAX_OFFLINE_WAIT_MS) return last;
            await waitForOnline(RETRY_INTERVAL_MS);
            offlineWaited += RETRY_INTERVAL_MS;
            continue;
        }
        offlineWaited = 0; // reconnected → reset the offline timer

        onlineAttempts++;
        try {
            last = await Promise.race([
                op(),
                new Promise<T>((_, reject) => setTimeout(() => reject(new Error("timeout")), 20000)),
            ]);
            if (isSuccess(last)) return last;
            // A 4xx means the record isn't there — no number of retries changes
            // that. Stop immediately so the caller surfaces a real error instead
            // of hammering a missing record for 20s (#3460).
            if (isPermanentFailure(last)) return last;
        } catch {
            last = null;
        }
        // Failed while online → wait the 2s cadence before the next attempt.
        await new Promise((r) => setTimeout(r, RETRY_INTERVAL_MS));
    }
    return last;
}

async function storeElConversationWithRetry(
    conversationId: string,
    elConversationId: string,
    attempts: number,
): Promise<boolean> {
    for (let i = 0; i < attempts; i++) {
        if (await storeElConversation(conversationId, elConversationId)) return true;
        // Linear backoff before the next attempt (skip after the last).
        if (i < attempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500 * (i + 1)));
        }
    }
    return false;
}

/** Static waiting-room clip — the recruiter greeting voice, served from the
 *  OurTeam asset CDN (VITE_ASSET_OURTEAM_BASE_URL) by gender + language:
 *  `/commons/voices/recruiters/<gender>-<lang>.mp3`. No backend call. */
function getWaitingRoomAudioUrl(language?: string, voice?: string): string {
    // `language` is a LANGUAGES[].value ("en-US" | "English - Malaysian" | "Malay"
    // | "id-ID" | "vi-VN"). Both Malaysian options share the "my" voice — mirrors
    // LANGUAGES[].setupVoice in src/types/languages.ts.
    const byValue: Record<string, "en" | "id" | "my" | "vn"> = {
        "en-US": "en",
        "English - Malaysian": "my",
        Malay: "my",
        "id-ID": "id",
        "vi-VN": "vn",
    };
    const lc = (language ?? "").toLowerCase();
    const langCode =
        byValue[language ?? ""] ??
        (lc.startsWith("id")
            ? "id"
            : lc.includes("malay") || lc.startsWith("ms") || lc.startsWith("my")
                ? "my"
                : lc.startsWith("vi") || lc.startsWith("vn")
                    ? "vn"
                    : "en");
    const gender = voice === "male" || voice === "female" ? voice : "female";
    return `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/voices/recruiters/${gender}-${langCode}.mp3`;
}
